import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import baseUrl from "../../../../api/baseUrl";

type DentistRow = {
  dentistName: string;
  day: string;
  sessionName: string;
  startTime: string;
  endTime: string;
  roomNo: string;
  doctorId?: string;
  roomId?: string;
};

type GeneralSession = {
  sessionName: string;
  startTime: string; // HH:mm
  endTime: string; // HH:mm
};

type RoomOption = {
  _id: string;
  roomNumber: string;
  roomName: string;
  doctorsCount: number;
};

type DentistOption = { _id: string; name: string };

function SessionCombobox({
  id,
  labelId,
  value,
  options,
  loading,
  required,
  onSelect,
}: {
  id: string;
  labelId: string;
  value: string;
  options: GeneralSession[];
  loading: boolean;
  required?: boolean;
  onSelect: (s: GeneralSession) => void;
}) {
  const [open, setOpen] = useState(false);

  const selected = useMemo(
    () => options.find((s) => s.sessionName === value) ?? null,
    [options, value]
  );

  return (
    <div className="relative">
      <button
        id={id}
        type="button"
        aria-labelledby={labelId}
        onClick={() => setOpen((v) => !v)}
        className="w-full px-3 py-2 rounded-md border border-slate-200 bg-white text-left flex items-center justify-between gap-2 text-sm font-medium text-slate-800 outline-none focus:border-primary focus:ring-1 focus:ring-primary/40"
      >
        <div className="min-w-0 flex items-center justify-between gap-2 w-full">
          <span
            className={
              value ? "truncate text-slate-800" : "truncate text-slate-400"
            }
          >
            {loading
              ? "Loading…"
              : value ||
                (options.length === 0 ? "No sessions" : "Select session")}
          </span>
          {!loading && selected?.startTime && selected?.endTime ? (
            <span className="shrink-0 rounded-md bg-slate-100 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600 tabular-nums">
              {selected.startTime}
              <span className="mx-0.5 font-normal text-slate-400">·</span>
              {selected.endTime}
            </span>
          ) : null}
        </div>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-4 w-4 text-slate-500 shrink-0"
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {required ? (
        <input
          tabIndex={-1}
          value={value}
          onChange={() => {}}
          required
          className="pointer-events-none absolute opacity-0 h-0 w-0"
        />
      ) : null}

      {open ? (
        <div
          className="absolute z-[70] mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg max-h-56 overflow-auto"
          role="listbox"
          tabIndex={-1}
          onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        >
          {loading ? (
            <div className="px-3 py-2 text-sm text-slate-500">Loading…</div>
          ) : options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-slate-500">
              No sessions found.
            </div>
          ) : (
            options.map((s) => (
              <button
                type="button"
                key={s.sessionName}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onSelect(s);
                  setOpen(false);
                }}
                className="w-full text-left px-3 py-2 hover:bg-slate-50"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-sm font-medium text-slate-800">
                    {s.sessionName}
                  </div>
                  {s.startTime && s.endTime ? (
                    <span className="shrink-0 rounded-md bg-slate-100 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600 tabular-nums">
                      {s.startTime}
                      <span className="mx-0.5 font-normal text-slate-400">·</span>
                      {s.endTime}
                    </span>
                  ) : null}
                </div>
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

function RoomCombobox({
  id,
  labelId,
  value,
  options,
  loading,
  required,
  getStatus,
  onSelect,
}: {
  id: string;
  labelId: string;
  value: string;
  options: RoomOption[];
  loading: boolean;
  required?: boolean;
  getStatus: (roomNumber: string) => {
    capacity: number;
    used: number;
    available: number;
    isFull: boolean;
    bookedDentists: string[];
  } | null;
  onSelect: (r: RoomOption) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = useMemo(
    () => options.find((r) => r.roomNumber === value) ?? null,
    [options, value]
  );
  const selectedStatus = useMemo(
    () => (selected ? getStatus(selected.roomNumber) : null),
    [getStatus, selected]
  );

  return (
    <div className="relative">
      <button
        id={id}
        type="button"
        aria-labelledby={labelId}
        onClick={() => setOpen((v) => !v)}
        className="w-full px-3 py-2 rounded-md border border-slate-200 bg-white text-left flex items-center justify-between gap-2 text-sm font-medium text-slate-800 outline-none focus:border-primary focus:ring-1 focus:ring-primary/40"
      >
        <div className="min-w-0 flex items-center justify-between gap-2 w-full">
          <span className={value ? "truncate text-slate-800" : "truncate text-slate-400"}>
            {loading
              ? "Loading…"
              : value || (options.length === 0 ? "No rooms" : "Select room")}
          </span>
          {!loading && selected ? (
            <span className="shrink-0 rounded-md bg-slate-100 px-2 py-1 text-[11px] font-semibold uppercase tracking-wide text-slate-600 tabular-nums">
              {selectedStatus
                ? `${selectedStatus.available}/${selectedStatus.capacity} free`
                : `${selected.doctorsCount} doctors`}
            </span>
          ) : null}
        </div>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-4 w-4 text-slate-500 shrink-0"
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {required ? (
        <input
          tabIndex={-1}
          value={value}
          onChange={() => {}}
          required
          className="pointer-events-none absolute opacity-0 h-0 w-0"
        />
      ) : null}

      {open ? (
        <div className="absolute z-[70] mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg max-h-56 overflow-auto">
          {loading ? (
            <div className="px-3 py-2 text-sm text-slate-500">Loading…</div>
          ) : options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-slate-500">No rooms found.</div>
          ) : (
            options.map((r) => (
              (() => {
                const st = getStatus(r.roomNumber);
                const disabled = Boolean(st?.isFull);
                return (
              <button
                type="button"
                key={r.roomNumber}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  if (disabled) {
                    toast.error("Room is full for selected time.");
                    return;
                  }
                  onSelect(r);
                  setOpen(false);
                }}
                className={`w-full text-left px-3 py-2 hover:bg-slate-50 ${
                  disabled ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800">
                      Room {r.roomNumber}
                    </div>
                    {r.roomName ? (
                      <div className="text-xs text-slate-500 truncate">
                        {r.roomName}
                      </div>
                    ) : null}
                  </div>
                  <div className="shrink-0 text-xs text-slate-600 tabular-nums">
                    {st ? (
                      st.isFull ? (
                        <span className="font-semibold text-red-600">Full</span>
                      ) : (
                        `${st.available}/${st.capacity} free`
                      )
                    ) : (
                      `${r.doctorsCount} doctors`
                    )}
                  </div>
                </div>
              </button>
                );
              })()
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

function ensureDrPrefix(name: string): string {
  const n = String(name ?? "").trim();
  if (!n) return "";
  if (/^dr\.?\s+/i.test(n)) return n.replace(/^dr\.?\s+/i, "Dr. ");
  return `Dr. ${n}`;
}

function buildHalfHourTimeOptions(): string[] {
  const slots: string[] = [];
  for (let h = 0; h < 24; h += 1) {
    const hh = String(h).padStart(2, "0");
    slots.push(`${hh}:00`);
    slots.push(`${hh}:30`);
  }
  return slots;
}

const HALF_HOUR_OPTIONS = buildHalfHourTimeOptions();

function timeToMinutes(t: string): number {
  const m = String(t).trim().match(/^(\d{1,2}):(\d{2})/);
  if (!m) return NaN;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}

function flexibleTimeToMinutes(raw: string): number {
  const str = String(raw ?? "").trim();
  if (!str) return NaN;
  const hhmm = str.match(/^(\d{1,2}):(\d{2})$/);
  if (hhmm) return parseInt(hhmm[1], 10) * 60 + parseInt(hhmm[2], 10);

  const mer = str.toLowerCase().match(/\b(am|pm)\b/);
  const isPm = mer?.[1] === "pm";
  const isAm = mer?.[1] === "am";
  const cleaned = str.toLowerCase().replace(/\b(am|pm)\b/gi, "").trim();
  const m = cleaned.match(/^(\d{1,2})(?::(\d{2}))?$/);
  if (!m) return NaN;
  let h = parseInt(m[1], 10);
  let min = parseInt(m[2] ?? "0", 10);
  if (Number.isNaN(h)) return NaN;
  if (Number.isNaN(min)) min = 0;
  min = Math.min(59, Math.max(0, min));
  if (isPm && h < 12) h += 12;
  if (isAm && h === 12) h = 0;
  return h * 60 + min;
}

function rangesOverlap(aStart: number, aEnd: number, bStart: number, bEnd: number) {
  return aStart < bEnd && bStart < aEnd;
}

function DentistCombobox({
  id,
  labelId,
  value,
  options,
  loading,
  onSelect,
  required,
}: {
  id: string;
  labelId: string;
  value: string;
  options: DentistOption[];
  loading: boolean;
  onSelect: (d: DentistOption) => void;
  required?: boolean;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        id={id}
        type="button"
        aria-labelledby={labelId}
        onClick={() => setOpen((v) => !v)}
        className="w-full px-3 py-2 rounded-md border border-slate-200 bg-white text-left flex items-center justify-between gap-2 text-sm font-medium text-slate-800 outline-none focus:border-primary focus:ring-1 focus:ring-primary/40"
      >
        <span className={value ? "text-slate-800" : "text-slate-400"}>
          {loading
            ? "Loading…"
            : value || (options.length === 0 ? "No dentists" : "Select dentist")}
        </span>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-4 w-4 text-slate-500"
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {required ? (
        <input
          tabIndex={-1}
          value={value}
          onChange={() => {}}
          required
          className="pointer-events-none absolute opacity-0 h-0 w-0"
        />
      ) : null}

      {open ? (
        <div className="absolute z-[70] mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg max-h-56 overflow-auto">
          {loading ? (
            <div className="px-3 py-2 text-sm text-slate-500">Loading…</div>
          ) : options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-slate-500">
              No dentists found.
            </div>
          ) : (
            options.map((d) => (
              <button
                type="button"
                key={d._id}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onSelect(d);
                  setOpen(false);
                }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
              >
                {d.name}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

function SimpleCombobox({
  id,
  labelId,
  value,
  options,
  placeholder,
  required,
  onSelect,
}: {
  id: string;
  labelId: string;
  value: string;
  options: string[];
  placeholder?: string;
  required?: boolean;
  onSelect: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);

  return (
    <div className="relative">
      <button
        id={id}
        type="button"
        aria-labelledby={labelId}
        onClick={() => setOpen((v) => !v)}
        className="w-full px-3 py-2 rounded-md border border-slate-200 bg-white text-left flex items-center justify-between gap-2 text-sm font-medium text-slate-800 outline-none focus:border-primary focus:ring-1 focus:ring-primary/40"
      >
        <span className={value ? "text-slate-800" : "text-slate-400"}>
          {value || placeholder || "Select…"}
        </span>
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="h-4 w-4 text-slate-500"
          aria-hidden="true"
        >
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {required ? (
        <input
          tabIndex={-1}
          value={value}
          onChange={() => {}}
          required
          className="pointer-events-none absolute opacity-0 h-0 w-0"
        />
      ) : null}

      {open ? (
        <div className="absolute z-[70] mt-1 w-full rounded-md border border-slate-200 bg-white shadow-lg max-h-56 overflow-auto">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-slate-500">
              No results.
            </div>
          ) : (
            options.map((opt) => (
              <button
                type="button"
                key={opt}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => {
                  onSelect(opt);
                  setOpen(false);
                }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50"
              >
                {opt}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

const weekDays = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
];

function uniqueDentists(apiDentists: DentistOption[]): DentistOption[] {
  return apiDentists;
}

const initialRows: DentistRow[] = [];

export default function DentistGeneralHoursTab() {
  const [rows, setRows] = useState<DentistRow[]>(initialRows);
  const [filterDentist, setFilterDentist] = useState<string>("all");
  const [filterDay, setFilterDay] = useState<string>("all");

  const [apiDentists, setApiDentists] = useState<DentistOption[]>([]);
  const [dentistsLoading, setDentistsLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [deleteLoadingKey, setDeleteLoadingKey] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDentistsLoading(true);
    baseUrl
      .get("/client/setting/dentistGeneralHours/getDentists")
      .then((res) => {
        if (cancelled) return;
        const raw = res.data?.data?.dentists;
        const list: DentistOption[] = Array.isArray(raw)
          ? raw
              .map((d: unknown) => {
                const o = d as Record<string, unknown>;
                return {
                  _id: String(o?._id ?? "").trim(),
                  name: ensureDrPrefix(String(o?.name ?? "")),
                };
              })
              .filter((d: DentistOption) => d._id && d.name)
          : [];
        setApiDentists(list);
      })
      .catch((err: { response?: { data?: { message?: string; error?: string } } }) => {
        if (cancelled) return;
        setApiDentists([]);
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Failed to load dentists"
        );
      })
      .finally(() => {
        if (!cancelled) setDentistsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const dentistOptions = useMemo(() => uniqueDentists(apiDentists), [apiDentists]);
  const dentistNameOptions = useMemo(() => {
    const s = new Set<string>();
    rows.forEach((r) => {
      const n = String(r.dentistName ?? "").trim();
      if (n) s.add(n);
    });
    return Array.from(s);
  }, [rows]);

  const refreshTable = async () => {
    setTableLoading(true);
    try {
      const res = await baseUrl.get("/client/setting/dentistGeneralHours/list");
      const raw = res.data?.data?.sessions;
      const list: DentistRow[] = Array.isArray(raw)
        ? raw
            .map((x: unknown) => {
              const o = x as Record<string, unknown>;
              return {
                dentistName: ensureDrPrefix(String(o?.dentistName ?? "")),
                day: String(o?.day ?? ""),
                sessionName: String(o?.sessionName ?? ""),
                startTime: String(o?.startTime ?? ""),
                endTime: String(o?.endTime ?? ""),
                roomNo: String(o?.roomNo ?? ""),
                doctorId: String(o?.doctorId ?? ""),
                roomId: String(o?.roomId ?? ""),
              };
            })
            .filter((r: DentistRow) => r.dentistName && r.sessionName && r.day)
        : [];
      setRows(list);
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string; error?: string } } };
      toast.error(
        ax?.response?.data?.message ||
          ax?.response?.data?.error ||
          "Failed to load dentist general hours"
      );
      setRows([]);
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    refreshTable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDeleteRow = async (r: DentistRow) => {
    const doctorId = String(r.doctorId ?? "").trim();
    const roomId = String(r.roomId ?? "").trim();
    const day = String(r.day ?? "").trim();
    const sessionName = String(r.sessionName ?? "").trim();
    if (!doctorId || !roomId || !day || !sessionName) {
      toast.error("Missing row identifiers to delete.");
      return;
    }

    const key = `${doctorId}__${roomId}__${day}__${sessionName}`;
    if (
      !window.confirm(
        `Delete ${r.dentistName} — ${sessionName} (${day}) in room ${r.roomNo}?`
      )
    ) {
      return;
    }

    setDeleteLoadingKey(key);
    try {
      await baseUrl.post("/client/setting/dentistGeneralHours/deleteSession", {
        doctorId,
        roomId,
        day,
        sessionName,
      });
      toast.success("Session deleted");
      await refreshTable();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string; error?: string } } };
      toast.error(
        ax?.response?.data?.message ||
          ax?.response?.data?.error ||
          "Could not delete session"
      );
    } finally {
      setDeleteLoadingKey(null);
    }
  };
  const [generalSessions, setGeneralSessions] = useState<GeneralSession[]>([]);
  const [sessionNamesLoading, setSessionNamesLoading] = useState(false);
  const sessionNameOptions = useMemo(() => generalSessions, [generalSessions]);

  const filteredRows = useMemo(() => {
    return rows.filter((r) => {
      if (filterDentist !== "all" && r.dentistName !== filterDentist) {
        return false;
      }
      if (filterDay !== "all" && r.day !== filterDay) {
        return false;
      }
      return true;
    });
  }, [rows, filterDentist, filterDay]);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [newRow, setNewRow] = useState({
    dentistName: dentistOptions[0]?.name ?? "",
    doctorId: dentistOptions[0]?._id ?? "",
    day: weekDays[0],
    sessionName: "",
    startTime: "",
    endTime: "",
    roomNo: "",
    roomId: "",
  });

  const [rooms, setRooms] = useState<RoomOption[]>([]);
  const [roomsLoading, setRoomsLoading] = useState(false);
  const [roomCapacityError, setRoomCapacityError] = useState<string | null>(
    null
  );

  useEffect(() => {
    if (!isCreateOpen) return;
    let cancelled = false;
    setRoomsLoading(true);
    baseUrl
      .get("/client/setting/clinicRoom/getRooms")
      .then((res) => {
        if (cancelled) return;
        const raw = res.data?.data;
        const list: RoomOption[] = Array.isArray(raw)
          ? raw
              .map((x: unknown) => {
                const o = x as Record<string, unknown>;
                return {
                  _id: String(o?._id ?? "").trim(),
                  roomNumber: String(o?.roomNumber ?? "").trim(),
                  roomName: String(o?.roomName ?? "").trim(),
                  doctorsCount: Number.isFinite(Number(o?.doctorsCount))
                    ? Number(o?.doctorsCount)
                    : 0,
                };
              })
              .filter((r) => r._id && r.roomNumber)
          : [];
        setRooms(list);
        setNewRow((p) =>
          p.roomNo
            ? p
            : {
                ...p,
                roomNo: list[0]?.roomNumber ?? "",
                roomId: list[0]?._id ?? "",
              }
        );
      })
      .catch((err: { response?: { data?: { message?: string; error?: string } } }) => {
        if (cancelled) return;
        setRooms([]);
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Failed to load rooms"
        );
      })
      .finally(() => {
        if (!cancelled) setRoomsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isCreateOpen]);

  const currentRoomStatus = useMemo(() => {
    const day = String(newRow.day ?? "").trim();
    const startM = flexibleTimeToMinutes(newRow.startTime);
    const endM = flexibleTimeToMinutes(newRow.endTime);
    const canCompute =
      day && Number.isFinite(startM) && Number.isFinite(endM) && endM > startM;

    const byRoom = new Map<
      string,
      { capacity: number; used: number; available: number; isFull: boolean; bookedDentists: string[] }
    >();

    for (const r of rooms) {
      const cap = Number.isFinite(Number(r.doctorsCount)) ? Number(r.doctorsCount) : 0;
      byRoom.set(r.roomNumber, {
        capacity: cap,
        used: 0,
        available: cap,
        isFull: false,
        bookedDentists: [],
      });
    }

    if (!canCompute) return byRoom;

    for (const existing of rows) {
      if (String(existing.day ?? "").trim() !== day) continue;
      const roomNo = String(existing.roomNo ?? "").trim();
      if (!roomNo) continue;
      const eStart = flexibleTimeToMinutes(existing.startTime);
      const eEnd = flexibleTimeToMinutes(existing.endTime);
      if (!Number.isFinite(eStart) || !Number.isFinite(eEnd)) continue;
      if (!rangesOverlap(startM, endM, eStart, eEnd)) continue;

      const st = byRoom.get(roomNo);
      if (!st) continue;
      st.used += 1;
      st.bookedDentists.push(String(existing.dentistName ?? "").trim());
    }

    for (const [roomNo, st] of Array.from(byRoom.entries())) {
      st.available = Math.max(0, st.capacity - st.used);
      st.isFull = st.capacity > 0 ? st.available <= 0 : st.used > 0;
      st.bookedDentists = Array.from(new Set(st.bookedDentists.filter(Boolean)));
      byRoom.set(roomNo, st);
    }

    return byRoom;
  }, [newRow.day, newRow.startTime, newRow.endTime, rooms, rows]);

  const getRoomStatus = useCallback(
    (roomNumber: string) => currentRoomStatus.get(roomNumber) ?? null,
    [currentRoomStatus]
  );

  useEffect(() => {
    const roomNo = String(newRow.roomNo ?? "").trim();
    if (!roomNo) {
      setRoomCapacityError(null);
      return;
    }
    const st = getRoomStatus(roomNo);
    if (!st) {
      setRoomCapacityError(null);
      return;
    }
    if (st.isFull) {
      setRoomCapacityError(
        `Room ${roomNo} is full for the selected time (${st.used}/${st.capacity} doctors).`
      );
    } else if (st.capacity > 0 && (newRow.startTime || newRow.endTime)) {
      setRoomCapacityError(
        `Available doctors: ${st.available}/${st.capacity}${
          st.bookedDentists.length ? ` (Booked: ${st.bookedDentists.join(", ")})` : ""
        }`
      );
    } else {
      setRoomCapacityError(null);
    }
  }, [newRow.roomNo, newRow.day, newRow.startTime, newRow.endTime, getRoomStatus]);

  const resetNewRow = () => {
    const firstSession = sessionNameOptions[0] ?? null;
    const firstDentist = dentistOptions[0] ?? null;
    const firstRoom = rooms[0] ?? null;
    setNewRow({
      dentistName: firstDentist?.name ?? "",
      doctorId: firstDentist?._id ?? "",
      day: weekDays[0],
      sessionName: firstSession?.sessionName ?? "",
      startTime: firstSession?.startTime ?? "",
      endTime: firstSession?.endTime ?? "",
      roomNo: firstRoom?.roomNumber ?? "",
      roomId: firstRoom?._id ?? "",
    });
  };

  const openCreate = () => {
    resetNewRow();
    setIsCreateOpen(true);
  };

  const closeCreate = () => {
    setIsCreateOpen(false);
    resetNewRow();
  };

  useEffect(() => {
    if (!isCreateOpen) return;
    const day = String(newRow.day ?? "").trim() || weekDays[0];
    let cancelled = false;
    setSessionNamesLoading(true);
    baseUrl
      .get("/client/setting/dentistGeneralHours/getGeneralSessionNames", {
        params: { day },
      })
      .then((res) => {
        if (cancelled) return;
        const raw = res.data?.data?.sessions;
        const list: GeneralSession[] = Array.isArray(raw)
          ? raw
              .map((x: unknown) => {
                const o = x as Record<string, unknown>;
                return {
                  sessionName: String(o?.sessionName ?? "").trim(),
                  startTime: String(o?.startTime ?? "").trim(),
                  endTime: String(o?.endTime ?? "").trim(),
                };
              })
              .filter((s) => s.sessionName)
          : [];
        setGeneralSessions(list);
        setNewRow((p) => {
          const first = list[0];
          if (!first) return { ...p, sessionName: "", startTime: "", endTime: "" };
          return {
            ...p,
            sessionName: first.sessionName,
            startTime: first.startTime ?? "",
            endTime: first.endTime ?? "",
          };
        });
      })
      .catch((err: { response?: { data?: { message?: string; error?: string } } }) => {
        if (cancelled) return;
        setGeneralSessions([]);
        toast.error(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Failed to load session names"
        );
      })
      .finally(() => {
        if (!cancelled) setSessionNamesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isCreateOpen, newRow.day]);

  const sessionTemplate = useMemo(() => {
    const name = newRow.sessionName?.trim();
    if (!name) return null;
    return generalSessions.find((s) => s.sessionName === name) ?? null;
  }, [generalSessions, newRow.sessionName]);

  const startTimeOptions = useMemo(() => {
    const sm = timeToMinutes(sessionTemplate?.startTime ?? "");
    const em = timeToMinutes(sessionTemplate?.endTime ?? "");
    if (!Number.isFinite(sm) || !Number.isFinite(em)) return HALF_HOUR_OPTIONS;
    return HALF_HOUR_OPTIONS.filter((t) => {
      const m = timeToMinutes(t);
      return Number.isFinite(m) && m >= sm && m < em;
    });
  }, [sessionTemplate]);

  const endTimeOptions = useMemo(() => {
    const sm = timeToMinutes(sessionTemplate?.startTime ?? "");
    const em = timeToMinutes(sessionTemplate?.endTime ?? "");
    const curStart = timeToMinutes(newRow.startTime ?? "");
    if (!Number.isFinite(sm) || !Number.isFinite(em)) return HALF_HOUR_OPTIONS;
    return HALF_HOUR_OPTIONS.filter((t) => {
      const m = timeToMinutes(t);
      if (!Number.isFinite(m)) return false;
      if (Number.isFinite(curStart) && m <= curStart) return false;
      return m > sm && m <= em;
    });
  }, [sessionTemplate, newRow.startTime]);

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed: DentistRow = {
      dentistName: ensureDrPrefix(newRow.dentistName),
      day: newRow.day.trim(),
      sessionName: newRow.sessionName.trim(),
      startTime: newRow.startTime.trim(),
      endTime: newRow.endTime.trim(),
      roomNo: newRow.roomNo.trim(),
      doctorId: String((newRow as any).doctorId ?? "").trim(),
      roomId: String((newRow as any).roomId ?? "").trim(),
    };
    if (
      !trimmed.dentistName ||
      !trimmed.sessionName ||
      !trimmed.day ||
      !trimmed.roomNo
    )
      return;

    const st = getRoomStatus(trimmed.roomNo);
    if (st?.isFull) {
      toast.error("Room is full for selected time.");
      return;
    }

    try {
      await baseUrl.post("/client/setting/dentistGeneralHours/create", {
        doctorId: trimmed.doctorId,
        day: trimmed.day,
        sessionName: trimmed.sessionName,
        startTime: trimmed.startTime,
        endTime: trimmed.endTime,
        roomId: trimmed.roomId,
      });
      toast.success("Saved");
      await refreshTable();
      closeCreate();
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { message?: string; error?: string } } };
      toast.error(
        ax?.response?.data?.message ||
          ax?.response?.data?.error ||
          "Could not save"
      );
    }
  };

  return (
    <div className="w-full bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden sm:mx-auto sm:max-w-6xl">
      <div className="px-4 py-3 border-b border-slate-200 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-sm font-semibold text-slate-800">
          Dentist General hours
        </h3>
        <button
          type="button"
          onClick={openCreate}
          className="px-3 py-1.5 rounded-md text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 active:bg-slate-700 transition-colors w-full sm:w-auto"
        >
          Create Session
        </button>
      </div>

      <div className="px-4 pt-3 pb-2 flex flex-col sm:flex-row sm:flex-wrap gap-3 sm:items-end sm:gap-4 border-b border-slate-100">
        <label className="space-y-1 block w-full sm:w-auto sm:min-w-[200px]">
          <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
            Dentist
          </div>
          <select
            value={filterDentist}
            onChange={(e) => setFilterDentist(e.target.value)}
            className="w-full px-3 py-1.5 rounded-md text-sm border border-slate-200 bg-white text-slate-800 outline-none focus:border-primary focus:ring-1 focus:ring-primary/40"
          >
            <option value="all">All dentists</option>
            {dentistNameOptions.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <label className="space-y-1 block w-full sm:w-auto sm:min-w-[200px]">
          <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
            Day
          </div>
          <select
            value={filterDay}
            onChange={(e) => setFilterDay(e.target.value)}
            className="w-full px-3 py-1.5 rounded-md text-sm border border-slate-200 bg-white text-slate-800 outline-none focus:border-primary focus:ring-1 focus:ring-primary/40"
          >
            <option value="all">All days</option>
            {weekDays.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-center">
          <thead>
            <tr className="bg-slate-50/80">
              <th className="px-2 py-2 text-xs font-semibold text-slate-600 tracking-wide min-w-[160px]">
                Dentist
              </th>
              <th className="px-2 py-2 text-xs font-semibold text-slate-600 tracking-wide min-w-[140px]">
                Day
              </th>
              <th className="px-2 py-2 text-xs font-semibold text-slate-600 tracking-wide min-w-[200px]">
                Session Name
              </th>
              <th className="px-2 py-2 text-xs font-semibold text-slate-600 tracking-wide min-w-[120px]">
                Start Time
              </th>
              <th className="px-2 py-2 text-xs font-semibold text-slate-600 tracking-wide min-w-[120px]">
                End Time
              </th>
              <th className="px-2 py-2 text-xs font-semibold text-slate-600 tracking-wide min-w-[120px]">
                Room No
              </th>
              <th className="px-2 py-2 text-xs font-semibold text-slate-600 tracking-wide min-w-[120px]">
                Action
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {tableLoading ? (
              <tr>
                <td
                  className="px-3 py-6 text-sm text-slate-500"
                  colSpan={7}
                >
                  Loading…
                </td>
              </tr>
            ) : filteredRows.length === 0 ? (
              <tr>
                <td
                  className="px-3 py-6 text-sm text-slate-500"
                  colSpan={7}
                >
                  No rows.
                </td>
              </tr>
            ) : (
              filteredRows.map((r, idx) => (
              <tr
                key={`${r.doctorId ?? r.dentistName}-${r.roomId ?? r.roomNo}-${r.day}-${r.sessionName}-${idx}`}
                className="hover:bg-slate-50/50"
              >
                <td className="px-2 py-2 text-sm font-semibold text-slate-800">
                  {r.dentistName}
                </td>
                <td className="px-2 py-2 text-sm font-medium text-slate-700">
                  {r.day || "—"}
                </td>
                <td className="px-2 py-2 text-sm font-medium text-slate-700">
                  {r.sessionName}
                </td>
                <td className="px-2 py-2 text-sm text-slate-600">{r.startTime}</td>
                <td className="px-2 py-2 text-sm text-slate-600">{r.endTime}</td>
                <td className="px-2 py-2 text-sm font-medium text-slate-700">
                  {r.roomNo}
                </td>
                <td className="px-2 py-2">
                  <button
                    type="button"
                    onClick={() => onDeleteRow(r)}
                    disabled={
                      deleteLoadingKey ===
                      `${String(r.doctorId ?? "").trim()}__${String(
                        r.roomId ?? ""
                      ).trim()}__${String(r.day ?? "").trim()}__${String(
                        r.sessionName ?? ""
                      ).trim()}`
                    }
                    className="px-2 py-1 rounded text-xs font-semibold text-red-700 bg-red-50 hover:bg-red-100 disabled:opacity-50"
                  >
                    {deleteLoadingKey ===
                    `${String(r.doctorId ?? "").trim()}__${String(
                      r.roomId ?? ""
                    ).trim()}__${String(r.day ?? "").trim()}__${String(
                      r.sessionName ?? ""
                    ).trim()}`
                      ? "…"
                      : "Delete"}
                  </button>
                </td>
              </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {isCreateOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4"
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            onClick={closeCreate}
            aria-label="Close modal"
          />
          <div className="relative w-full max-w-sm sm:max-w-md max-h-[90vh] overflow-visible">
            <form
              onSubmit={onSave}
              className="rounded-xl bg-white shadow-lg border border-slate-200 overflow-visible"
            >
              <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                <div className="text-sm font-semibold text-slate-800">
                  Create Session
                </div>
                <button
                  type="button"
                  onClick={closeCreate}
                  aria-label="Close"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-5 w-5"
                    aria-hidden="true"
                  >
                    <path d="M18 6 6 18" />
                    <path d="m6 6 12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-4 space-y-3">
                <div className="space-y-1 block">
                  <div
                    id="create-dentist-label"
                    className="text-xs font-semibold text-slate-600"
                  >
                    Dentist
                  </div>
                  <DentistCombobox
                    id="create-dentist"
                    labelId="create-dentist-label"
                    value={newRow.dentistName}
                    options={dentistOptions}
                    loading={dentistsLoading}
                    required
                    onSelect={(d) =>
                      setNewRow((p) => ({
                        ...p,
                        dentistName: d.name,
                        doctorId: d._id,
                      }))
                    }
                  />
                </div>

                <label className="space-y-1 block">
                  <div className="text-xs font-semibold text-slate-600">Day</div>
                  <div className="space-y-1">
                    <div
                      id="create-day-label"
                      className="sr-only"
                    >
                      Day
                    </div>
                    <SimpleCombobox
                      id="create-day"
                      labelId="create-day-label"
                      value={newRow.day}
                      options={weekDays}
                      placeholder="Search day…"
                      required
                      onSelect={(d) => setNewRow((p) => ({ ...p, day: d }))}
                    />
                  </div>
                </label>

                <label className="space-y-1 block">
                  <div className="text-xs font-semibold text-slate-600">
                    Session Name
                  </div>
                  <div id="create-session-label" className="sr-only">
                    Session Name
                  </div>
                  <SessionCombobox
                    id="create-session"
                    labelId="create-session-label"
                    value={newRow.sessionName}
                    options={sessionNameOptions}
                    loading={sessionNamesLoading}
                    required
                    onSelect={(s) =>
                      setNewRow((p) => ({
                        ...p,
                        sessionName: s.sessionName,
                        startTime: s.startTime ?? "",
                        endTime: s.endTime ?? "",
                      }))
                    }
                  />
                  {!sessionNamesLoading && sessionNameOptions.length === 0 ? (
                    <p className="text-xs text-slate-500">
                      No sessions for this day (create in General Open Hours)
                    </p>
                  ) : null}
                </label>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label className="space-y-1">
                    <div className="text-xs font-semibold text-slate-600">
                      Start Time
                    </div>
                    <div id="create-start-time-label" className="sr-only">
                      Start Time
                    </div>
                    <SimpleCombobox
                      id="create-start-time"
                      labelId="create-start-time-label"
                      value={newRow.startTime}
                      options={startTimeOptions}
                      placeholder="Search time…"
                      required
                      onSelect={(t) =>
                        setNewRow((p) => ({
                          ...p,
                          startTime: t,
                          endTime:
                            p.endTime && timeToMinutes(p.endTime) > timeToMinutes(t)
                              ? p.endTime
                              : "",
                        }))
                      }
                    />
                  </label>

                  <label className="space-y-1">
                    <div className="text-xs font-semibold text-slate-600">
                      End Time
                    </div>
                    <div id="create-end-time-label" className="sr-only">
                      End Time
                    </div>
                    <SimpleCombobox
                      id="create-end-time"
                      labelId="create-end-time-label"
                      value={newRow.endTime}
                      options={endTimeOptions}
                      placeholder="Search time…"
                      required
                      onSelect={(t) => setNewRow((p) => ({ ...p, endTime: t }))}
                    />
                  </label>
                </div>

                <label className="space-y-1 block">
                  <div className="text-xs font-semibold text-slate-600">
                    Room No
                  </div>
                  <div id="create-room-label" className="sr-only">
                    Room No
                  </div>
                  <RoomCombobox
                    id="create-room"
                    labelId="create-room-label"
                    value={newRow.roomNo}
                    options={rooms}
                    loading={roomsLoading}
                    required
                    getStatus={getRoomStatus}
                    onSelect={(r) =>
                      setNewRow((p) => ({
                        ...p,
                        roomNo: r.roomNumber,
                        roomId: r._id,
                      }))
                    }
                  />
                  {roomCapacityError ? (
                    <p
                      className={
                        getRoomStatus(newRow.roomNo)?.isFull
                          ? "text-xs text-red-600"
                          : "text-xs text-slate-500"
                      }
                    >
                      {roomCapacityError}
                    </p>
                  ) : null}
                </label>
              </div>

              <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={closeCreate}
                  className="px-3 py-1.5 rounded-md text-sm font-semibold bg-slate-100 text-slate-700 hover:bg-slate-200 active:bg-slate-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-3 py-1.5 rounded-md text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 active:bg-slate-700 transition-colors"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
